import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useReducer, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import Pagination from "../../../components/ui/Pagination";
import RenderTable from "../../../components/ui/RenderTable";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../../context/bulkDeleteReducer";
import { Chip, pluralize, toQuery } from "../../../helpers/general";
import { useCookie } from "../../../hooks/useCookie";
import { useDebounce } from "../../../hooks/useDebounce";
import { useToast } from "../../../hooks/useToast";
import { general } from "../../../locales/general";
import apiService from "../../../service/apiService";
import AdminRoute from "../../auth/RestrictedRoute";
import TaxiCouponsTable from "./TaxiCouponsTable";
import { cardStyle } from "../../Places/PlacesTable";
import { controlStyle } from "../../../helpers/constants";

interface Coupons {
  meta: PaginationMeta;
  items: Coupon[];
}

type SortOrder = "ASC" | "DESC";

const SORT_FIELDS = [
  { value: "createdAt", label: "Date de création" },
  { value: "name", label: "Nom" },
  { value: "discount", label: "Réduction" },
];



export default function TaxiDiscounts() {
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("DESC");

  const searchQuery = useDebounce(query, 400);

  const endpoint = `taxi-coupons${toQuery({
    page: currentPage,
    query: searchQuery,
    field: sortField,
    order: sortOrder,
  })}`;

  const { data, isLoading } = useQuery<Coupons>({
    queryKey: ["getTaxiCoupons", endpoint],
    queryFn: () => apiService.MakeGetRequest(endpoint, token),
    keepPreviousData: true,
  });

  const deleteCouponMutation = useMutation({
    mutationFn: (id: string) =>
      apiService.MakeDeleteRequest(`taxi-coupons/${id}`, token),
  });

  const [ , dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );

  const deleteCoupon = (id: string) =>
    deleteCouponMutation.mutate(id, {
      onSuccess: () => {
        toastSuccess("Coupon supprimé avec succès");
        // Prefix key: refreshes every page / sort / search variant
        queryClient.invalidateQueries(["getTaxiCoupons"]);
      },
      onError: () => toastError(general.fr.message.operationFailed),
    });

  // Any filter change goes back to page 1
  const changeSort = (field: string, order: SortOrder) => {
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const coupons = data?.items ?? [];
  const total = data?.meta?.totalItems ?? 0;

  return (
    <AdminRoute>
      <section>
        {/* Title + count */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fs-4 fw-bold mb-0">Coupons de taxi</h5>
            {data && (
              <Chip tone="warning">
                {total} {pluralize("coupon", total)}
              </Chip>
            )}
          </div>

          <Link
            to="/taxi/discounts/new"
            className="btn btn-primary text-white rounded-pill px-4"
          >
            + Ajouter un coupon
          </Link>
        </div>

        {/* Toolbar */}
        <div
          style={{ ...cardStyle, padding: "12px 16px" }}
          className="d-flex flex-wrap align-items-center gap-2 mb-3"
        >
          <div
            className="d-flex align-items-center gap-2 px-3 flex-grow-1"
            style={{ ...controlStyle, minWidth: 220, maxWidth: 360 }}
          >
            <MagnifyingGlassIcon color="gray" width={18} />
            <input
              type="text"
              placeholder="Rechercher un coupon"
              className="border-0 w-100 bg-transparent"
              style={{ outline: "none", fontSize: 13 }}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value.toLowerCase());
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="ms-auto d-flex align-items-center gap-2">
            <span className="text-muted" style={{ fontSize: 12 }}>
              Trier par
            </span>
            <select
              className="form-select px-3"
              style={{ ...controlStyle, width: "auto" }}
              value={sortField}
              onChange={(e) => changeSort(e.target.value, sortOrder)}
            >
              {SORT_FIELDS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="border-0 px-3 fw-semibold"
              style={{ ...controlStyle, color: "#444" }}
              title={sortOrder === "ASC" ? "Ascendant" : "Descendant"}
              onClick={() =>
                changeSort(sortField, sortOrder === "ASC" ? "DESC" : "ASC")
              }
            >
              {sortOrder === "ASC" ? "↑ Asc" : "↓ Desc"}
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <RenderTable
            loading={isLoading}
            render={() =>
              coupons.length > 0 ? (
                <TaxiCouponsTable
                  coupons={coupons}
                  TaxiCouponsTableToBeDeleted={dispatch}
                  deleteCoupon={deleteCoupon}
                  isDeleting={deleteCouponMutation.isLoading}
                />
              ) : (
                <p className="text-center text-muted py-5 mb-0">
                  {general.fr.noData}
                </p>
              )
            }
          />
        </div>

        <Pagination
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          siblingCount={1}
          totalCount={data?.meta?.totalPages || 1}
          pageSize={10}
        />
      </section>
    </AdminRoute>
  );
}
